/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector(`[data-id="${id}"]`);
      this.page = 1;
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        this.updateProductCount();
      });

      if (this.classList.contains('pagination-type--loadmore') && this.button) {
        this.initLoadMore();
      }

      if (this.classList.contains('pagination-type--infinite')) {
        this.initInfiniteScroll();
      }
    }

    addUrlParam(search, key) {
      const newParam = `${key}=${this.page}`;
      if (!search) return `?${newParam}`;

      const params = search.replace(new RegExp(`([?&])${key}[^&]*`), `$1${newParam}`);
      return params === search ? `${search}&${newParam}` : params;
    }

    initLoadMore() {
      if (!this.button) return;

      this.button.addEventListener('click', () => {
        this.loadProducts();
        this.button.blur();
      });
    }

    initInfiniteScroll() {
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].intersectionRatio === 1) {
          this.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });

      this.observer.observe(this);
    }

    async loadProducts() {
      if (this.hasAttribute('loading')) return;

      this.page++;
      this.setAttribute('loading', 'true');

      const url = `${document.location.pathname}${this.addUrlParam(document.location.search, 'page')}`;

      try {
        const response = await fetch(url);
        const responseText = await response.text();

        this.renderProducts(responseText, url);

        dispatchCustomEvent('pagination:page-changed', { url });
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        this.removeAttribute('loading');
      }
    }

    renderProducts(html, url) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const gridToReplace = parsedHTML.getElementById('product-grid');

      if (!gridToReplace) {
        this.hidePagination();
        return;
      }

      const products = gridToReplace.querySelectorAll('.columns:not(.loading-overlay)');

      if (!products.length) {
        this.hidePagination();
        return;
      }

      products.forEach((product) => {
        this.grid.appendChild(product);

        product.querySelectorAll('.lazyload').forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      });

      this.updateProductCount();
    }

    updateProductCount() {
      const productCount = document.getElementById('ProductCount');
      const productCountLabel = document.querySelector('[data-product-count-label]');
      const progress = document.querySelector('[data-pagination-progress]');

      if (!this.grid || !productCount || !productCountLabel) return;

      const totalProducts = parseInt(productCount.dataset.totalProducts, 10);

      if (!totalProducts) return;

      const visibleProducts = Array.from(this.grid.children).filter((item) => {
        return (
          item.classList.contains('columns') &&
          !item.classList.contains('promotion-column') &&
          !item.classList.contains('loading-overlay')
        );
      });

      const shownProducts = Math.min(visibleProducts.length, totalProducts);

      productCount.dataset.currentProducts = shownProducts;
      productCountLabel.textContent = `1-${shownProducts} of ${totalProducts} products`;

      if (progress) {
        progress.style.setProperty('--progress-count', shownProducts / totalProducts);
      }

      if (shownProducts >= totalProducts) {
        this.hidePagination();
      }
    }

    hidePagination() {
      if (this.button) {
        this.button.style.display = 'none';
        this.button.setAttribute('hidden', 'hidden');
        this.button.setAttribute('disabled', 'disabled');
        this.button.classList.add('visually-hidden');
      }

      if (this.observer) {
        this.observer.unobserve(this);
      }

      this.classList.add('pagination--complete');
    }
  }

  customElements.define('pagination-theme', Pagination);
}